import {
  DEFAULT_ZONE_BORDER_COLOR,
  DEFAULT_ZONE_BORDER_WIDTH,
  DEFAULT_ZONE_FILL_OPACITY
} from "@gettactic/map-builder/src/components/map/defines";
import { MultilineSvgText } from "@gettactic/map-builder/src/components/ViewSVG/MultilineSvgText";
import type {
  ZoneShape,
  ZoneTypeDefinition
} from "@gettactic/map-builder/src/types";
import { stripHexAlpha } from "@gettactic/map-builder/src/utils/color";
import { sampleZonePolygonPoints } from "@gettactic/map-builder/src/utils/zonePolygon";
import React from "react";
import type { ZoneViewOptions } from "../RenderOptionsContext";
import { buildWorldShapeTransform } from "../shapeTransform";

export interface ZoneSvgProps {
  shape: ZoneShape;
  zoneTypes: ZoneTypeDefinition[];
  /** Viewer rules (legend visibility, hover, heat colours, links). */
  zoneView?: ZoneViewOptions;
}

/** Heat colours paint at a fixed opacity so the scale reads evenly. */
const HEAT_ZONE_FILL_OPACITY = 0.55;

export const ZoneSvg: React.FC<ZoneSvgProps> = ({
  shape,
  zoneTypes,
  zoneView
}) => {
  const heatColor = zoneView?.zoneColors?.[shape.id];
  // Viewer: category-tagged zones stay hidden until the legend switches
  // their category on; a zone given an explicit colour always draws.
  const isHiddenByLegend =
    !!zoneView &&
    !!shape.categoryTypeId &&
    !heatColor &&
    !zoneView.visibleCategories.has(shape.categoryTypeId);
  const category = shape.categoryTypeId
    ? zoneTypes.find((type) => type.id === shape.categoryTypeId)
    : undefined;
  // Opacity is owned by fillOpacity; alpha baked into old colours would
  // multiply with it into near-invisibility.
  const fillRaw = heatColor || category?.color || shape.color;
  const fill = fillRaw ? stripHexAlpha(fillRaw) : undefined;
  const stroke =
    heatColor ||
    category?.color ||
    shape.borderColor ||
    DEFAULT_ZONE_BORDER_COLOR;
  const baseFillOpacity = heatColor
    ? HEAT_ZONE_FILL_OPACITY
    : (shape.fillOpacity ?? DEFAULT_ZONE_FILL_OPACITY);
  // Legend hover spotlights one category: its zones brighten, the rest
  // fade. Linked zones keep a minimum fill so they are discoverable and
  // brighten under the pointer, as the current viewer does.
  let groupOpacity = 1;
  let fillOpacity = baseFillOpacity;
  if (zoneView?.categoryHover) {
    if (shape.categoryTypeId === zoneView.categoryHover) {
      fillOpacity = Math.min(1, baseFillOpacity * 3);
    } else {
      groupOpacity = 0.2;
    }
  }
  if (shape.link && zoneView) fillOpacity = Math.max(fillOpacity, 0.2);
  if (zoneView && zoneView.hoveredLinkZoneId === shape.id) {
    fillOpacity = Math.min(1, Math.max(baseFillOpacity * 3, 0.45));
  }
  const link = shape.link;
  const onLinkClick =
    zoneView?.onZoneLinkClick && link
      ? (event: React.MouseEvent<SVGGElement>) => {
          event.preventDefault();
          event.stopPropagation();
          zoneView.onZoneLinkClick?.(link);
        }
      : undefined;
  const strokeWidth = shape.borderWidth || DEFAULT_ZONE_BORDER_WIDTH;
  const maxRadius = Math.min(shape.width, shape.height) / 2;
  const radius = Math.min(shape.cornerRadius || 0, maxRadius);

  const polygonPoints = React.useMemo(() => {
    if ((shape.vertices?.length ?? 0) < 3) return null;
    return sampleZonePolygonPoints(
      shape.vertices as { x: number; y: number }[],
      shape.edges
    )
      .map((point) => `${shape.x + point.x},${shape.y + point.y}`)
      .join(" ");
  }, [shape.vertices, shape.edges, shape.x, shape.y]);

  if (isHiddenByLegend) return null;

  const labelPosition = shape.labelPosition || "top-left";
  const labelFontSize = shape.labelFontSize || 14;
  const labelTop =
    labelPosition === "center"
      ? shape.y + shape.height / 2 - labelFontSize / 2
      : labelPosition.startsWith("bottom")
        ? shape.y + shape.height - 8 - labelFontSize
        : shape.y + 8;

  return (
    <g
      transform={buildWorldShapeTransform(shape)}
      opacity={groupOpacity === 1 ? undefined : groupOpacity}
      onClick={onLinkClick}
      onMouseEnter={
        onLinkClick ? () => zoneView?.onLinkZoneHover?.(shape.id) : undefined
      }
      onMouseLeave={
        onLinkClick ? () => zoneView?.onLinkZoneHover?.(null) : undefined
      }
      style={onLinkClick ? { cursor: "pointer" } : undefined}
    >
      {polygonPoints ? (
        <polygon
          points={polygonPoints}
          fill={fill}
          fillOpacity={fillOpacity}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray="8 4"
        />
      ) : (
        <rect
          x={shape.x}
          y={shape.y}
          width={shape.width}
          height={shape.height}
          fill={fill}
          fillOpacity={fillOpacity}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray="8 4"
          rx={radius}
        />
      )}
      <MultilineSvgText
        x={shape.x + 8}
        y={labelTop}
        width={Math.max(shape.width - 16, labelFontSize)}
        // Konva does not clip wrapped label lines to the zone height.
        height={labelFontSize * 1000}
        textAnchor={labelPosition.endsWith("center") ? "middle" : "start"}
        fontFamily="Quicksand"
        style={{
          fontSize: `${labelFontSize}px`,
          fontFamily: "Quicksand",
          fontWeight: "400",
          fill: stroke
        }}
      >
        {shape.name || ""}
      </MultilineSvgText>
    </g>
  );
};
